<main class="container layout-39" aria-label="Personal Access Tokens">
  <div id="containerLeftMenu" style="margin-right: 15px">
    <left-menu [profileMenu]="true" />
  </div>
  <section id="column2">
    <div class="row">
      <div class="col col-12">
        <div class="d-flex justify-content-between align-items-center mb-3">
          <h1 class="mb-0 text-center w-100">PAT Setup</h1>
        </div>

        <div class="tokens-heading mb-3">
          <p class="tokens-subtitle mb-0">Manage the credentials used to access your Azure DevOps and GitHub repositories.</p>
          <a class="guide-link btn btn-sm btn-outline-danger" href="/profile/user-guide?chapter=13" target="_blank" rel="noopener noreferrer">
            <mat-icon aria-hidden="true">menu_book</mat-icon>
            Token setup guide
          </a>
        </div>

        <div class="security-note card shadow-sm mb-3">
          <mat-icon aria-hidden="true">lock</mat-icon>
          <span>Tokens are encrypted before they are stored. Only their status and expiration date are shown here.</span>
        </div>

        <section class="provider-grid" aria-label="Personal access token providers">
    @for (provider of providers; track provider) {
      @let state = getState(provider);
      <article class="provider-panel card shadow-sm" [class.provider-panel--github]="provider === 'github'">
        <div class="provider-panel__header card-header bg-white">
          <div>
            <div class="provider-kicker">{{ provider === 'github' ? 'Source control' : 'Work management and source control' }}</div>
            <h2>{{ providerLabel(provider) }}</h2>
            <p>{{ providerDescription(provider) }}</p>
          </div>
          <span class="status-pill" [class.status-pill--warning]="expiryWarning(provider) === 'expiring-soon'" [class.status-pill--danger]="expiryWarning(provider) === 'expired'">
            <span class="status-dot"></span>
            {{ statusLabel(provider) }}
          </span>
        </div>

        @if (state.isLoading) {
          <div class="panel-loading">
            <app-spinner [isLoading]="true" diameter="28" loadingText="Checking status..."></app-spinner>
          </div>
        } @else {
          @if (state.status?.hasPatConfigured && !state.showForm) {
            <div class="configured-summary">
              <div>
                <span class="summary-label">Last updated</span>
                <strong>{{ state.status?.updatedAt ? (state.status?.updatedAt | utcDate:'date') : 'Unknown' }}</strong>
              </div>
              <div>
                <span class="summary-label">Expires</span>
                <strong>{{ state.status?.expiresAt ? (state.status?.expiresAt | utcDate:'date') : 'No expiration' }}</strong>
              </div>
            </div>

            @if (expiryWarning(provider) === 'expired') {
              <div class="alert alert-danger">This token has expired. Replace it before starting work.</div>
            } @else if (expiryWarning(provider) === 'expiring-soon') {
              <div class="alert alert-warning">This token expires soon. Replace it before it expires.</div>
            }
          }

          @if (state.error) {
            <div class="alert alert-danger" role="alert">{{ state.error }}</div>
          }

          @if (state.validationMessage) {
            <div class="alert alert-success" role="status">{{ state.validationMessage }}</div>
          }

          @if (state.showForm) {
            <div class="token-form">
              <label class="form-label" [for]="provider + '-pat'">{{ state.status?.hasPatConfigured ? 'Replace' : 'Add' }} token</label>
              <div class="input-group mb-3">
                <input [id]="provider + '-pat'" class="form-control" [type]="state.showPat ? 'text' : 'password'" [(ngModel)]="state.patInput" (ngModelChange)="onPatInputChange(provider)" [disabled]="state.isSaving || state.isTesting" autocomplete="off" [placeholder]="'Paste your ' + providerLabel(provider) + ' token'">
                <button type="button" class="btn btn-outline-danger" (click)="state.showPat = !state.showPat" [attr.aria-label]="state.showPat ? 'Hide token' : 'Show token'">
                  {{ state.showPat ? 'Hide' : 'Show' }}
                </button>
              </div>

              <!-- Repository access is validated by the Test token action. -->

              <label class="form-label" [for]="provider + '-expiry'">Expiration (UTC)</label>
              <div class="expiry-row mb-3">
                <select [id]="provider + '-expiry'" class="form-control" [(ngModel)]="state.expiryPreset" (ngModelChange)="onExpiryPresetChange(provider)" [disabled]="state.isSaving">
                  <option value="30">30 days</option>
                  <option value="60">60 days</option>
                  <option value="90">90 days</option>
                  <option value="custom">Custom date</option>
                  <option value="">No expiration</option>
                </select>
                @if (state.expiryPreset === 'custom') {
                  <input [id]="provider + '-custom-expiry'" type="date" class="form-control" [(ngModel)]="state.expiresAtInput" [disabled]="state.isSaving">
                }
              </div>

              <div class="form-actions">
                <button type="button" class="btn btn-outline-danger" (click)="cancelForm(provider)" [disabled]="state.isSaving || state.isTesting">Cancel</button>
                <button type="button" class="btn btn-outline-danger" (click)="testToken(provider)" [disabled]="!state.patInput.trim() || state.isSaving || state.isTesting">
                  @if (state.isTesting) { <app-spinner [isLoading]="true" diameter="18" loadingText="Testing..." [showText]="false"></app-spinner> } @else { Test token }
                </button>
                <button type="button" class="btn btn-outline-danger" (click)="save(provider)" [disabled]="!state.patInput.trim() || !state.isValidated || state.isSaving || state.isTesting">
                  @if (state.isSaving) { <app-spinner [isLoading]="true" diameter="18" loadingText="Saving..." [showText]="false"></app-spinner> } @else { Save token }
                </button>
              </div>
            </div>
          } @else {
            <div class="panel-actions">
              <button type="button" class="btn btn-outline-danger" (click)="openForm(provider)" [disabled]="state.isTesting || state.isRemoving">{{ state.status?.hasPatConfigured ? 'Replace token' : 'Add token' }}</button>
              @if (state.status?.hasPatConfigured) {
                <button type="button" class="btn btn-outline-danger" (click)="testStoredToken(provider)" [disabled]="state.isTesting || state.isRemoving">
                  @if (state.isTesting) { <app-spinner [isLoading]="true" diameter="18" loadingText="Testing..." [showText]="false"></app-spinner> } @else { Test token }
                </button>
                <button type="button" class="btn btn-outline-danger" (click)="remove(provider)" [disabled]="state.isTesting || state.isRemoving">
                  @if (state.isRemoving) { Removing... } @else { Remove }
                </button>
              }
            </div>
          }
        }
      </article>
    }
        </section>
      </div>
    </div>
  </section>
</main>
